import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

@Component({
  selector: 'app-custome-year-picker',
  templateUrl: './custome-year-picker.component.html',
  styleUrls: ['./custome-year-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomeYearPickerComponent),
      multi: true
    }
  ]
})
export class CustomeYearPickerComponent implements ControlValueAccessor, OnInit {

  @Input() placeholder = 'Select Year';
  @Input() disabled = false;
  @Input() minYear?: number;
  @Input() maxYear?: number;
  @Output() yearChange = new EventEmitter<number>();

  isOpen = false;
  selectedYear?: number;
  currentYear = new Date().getFullYear();
  yearRange: number[] = [];
  currentYearRange = 0;
  inputValue: string = '';

  private onChange = (_: any) => {};
  private onTouched = () => {};

  ngOnInit() {
    this.generateYearRange();
  }

  toggleCalendar(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    this.onTouched();
    this.generateYearRange();
  }

  closeCalendar(): void {
    this.isOpen = false;
  }

  writeValue(value: number | null): void {
    if (value) {
      this.selectedYear = value;
      this.currentYear = value;
      this.inputValue = value.toString();
      this.generateYearRange();
    } else {
      this.selectedYear = undefined;
      this.inputValue = '';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  selectYear(year: number): void {
    this.selectedYear = year;
    this.inputValue = String(year);
    this.onChange(year);
    this.yearChange.emit(year);
    this.closeCalendar();
  }

  previousYearRange(): void {
    this.currentYearRange -= 12;
    this.generateYearRange();
  }

  nextYearRange(): void {
    this.currentYearRange += 12;
    this.generateYearRange();
  }

  generateYearRange(): void {
    const currentYear = this.currentYear;
    const startYear = Math.floor(currentYear / 12) * 12 + this.currentYearRange;
    this.yearRange = Array.from({ length: 12 }, (_, i) => startYear + i)
      .filter(y => (!this.minYear || y >= this.minYear) && (!this.maxYear || y <= this.maxYear));
  }

  getDisplayValue(): string {
    return this.selectedYear?.toString() ?? '';
  }

  isDisabled(year: number): boolean {
    if (this.minYear && year < this.minYear) return true;
    if (this.maxYear && year > this.maxYear) return true;
    return false;
  }

  // Manual input handlers
  handleManualInput(event: Event): void {
    if (this.disabled) return;
    const target = event.target as HTMLInputElement;
    this.inputValue = target.value.trim();

    // Only proceed when we have a 4-digit year
    const yearPattern = /^\d{4}$/;
    if (yearPattern.test(this.inputValue)) {
      let yearNum = Number(this.inputValue);

      // Enforce min/max if provided
      if (this.minYear && yearNum < this.minYear) yearNum = this.minYear;
      if (this.maxYear && yearNum > this.maxYear) yearNum = this.maxYear;

      this.selectedYear = yearNum;
      this.inputValue = String(yearNum);
      this.currentYear = yearNum;
      this.onChange(yearNum);
      this.yearChange.emit(yearNum);
      this.generateYearRange();
    }
  }

  filterNonNumeric(event: KeyboardEvent): void {
    if (this.disabled) return;
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'
    ];
    if (allowedKeys.includes(event.key)) return;
    if (!/\d/.test(event.key)) {
      event.preventDefault();
    }
  }

  commitManualInput(): void {
    if (this.disabled) return;
    if (!this.inputValue) {
      this.selectedYear = undefined;
      this.onChange(null);
      return;
    }
    const yearNum = Number(this.inputValue);
    if (!Number.isFinite(yearNum)) return;
    let finalYear = yearNum;
    if (this.minYear && finalYear < this.minYear) finalYear = this.minYear;
    if (this.maxYear && finalYear > this.maxYear) finalYear = this.maxYear;
    this.selectedYear = finalYear;
    this.inputValue = String(finalYear);
    this.currentYear = finalYear;
    this.onChange(finalYear);
    this.yearChange.emit(finalYear);
    this.generateYearRange();
  }
}